/**
 * FHIR-native scoring types for assessment templates.
 *
 * Aligns with Medplum's Questionnaire + Bot + ObservationDefinition pattern:
 * - Weights via HL7 itemWeight extension on answerOption.valueCoding (Coding context)
 * - Interpretation bands via ObservationDefinition.qualifiedInterval
 * - Risk flags via Flag resources (bot-created)
 * - Scores via Observation resources (bot-created, derivedFrom QuestionnaireResponse)
 */

// ---------------------------------------------------------------------------
// FHIR extension URLs
// ---------------------------------------------------------------------------

/** Standard HL7 extension for numeric scoring weight on answer options. */
export const ITEM_WEIGHT_URL = 'http://hl7.org/fhir/StructureDefinition/itemWeight';

/** Custom extension for storing the full scoring configuration JSON on a Questionnaire. */
export const SCORING_CONFIG_URL = 'https://unify.health/fhir/StructureDefinition/scoring-configuration';

/** Custom extension linking Observation to ObservationDefinition (when basedOn isn't available). */
export const OBS_DEF_LINK_URL = 'http://example.org/fhir/StructureDefinition/instantiates-observationdefinition';

// ---------------------------------------------------------------------------
// Scoring output (what Observations the Bot will create)
// ---------------------------------------------------------------------------

export interface ScoringOutput {
  id: string;
  label: string;
  codeSystem: string;
  codeValue: string;
  codeDisplay: string;
  valueType: 'integer' | 'decimal';
  /** Empty array = all scored items; non-empty = subset (subscale). */
  includedItemLinkIds: string[];
  transform: 'sum' | 'mean';
}

// ---------------------------------------------------------------------------
// Interpretation bands (maps to ObservationDefinition.qualifiedInterval)
// ---------------------------------------------------------------------------

export interface InterpretationBand {
  id: string;
  label: string;
  low: number;
  high: number;
}

/** Standard band colors assigned by position (lowest severity → highest). */
export const BAND_COLORS = [
  'var(--mantine-color-green-5)',
  'var(--mantine-color-blue-5)',
  'var(--mantine-color-yellow-5)',
  'var(--mantine-color-yellow-7)',
  'var(--mantine-color-red-5)',
] as const;

export function getBandColor(index: number): string {
  return BAND_COLORS[Math.min(index, BAND_COLORS.length - 1)];
}

// ---------------------------------------------------------------------------
// Risk flag triggers
// ---------------------------------------------------------------------------

export type RiskFlagComparison = '>=' | '>' | '<=' | '<' | '=';

export const RISK_FLAG_COMPARISONS: { value: RiskFlagComparison; label: string }[] = [
  { value: '>=', label: '>= (greater or equal)' },
  { value: '>', label: '> (greater than)' },
  { value: '<=', label: '<= (less or equal)' },
  { value: '<', label: '< (less than)' },
  { value: '=', label: '= (equals)' },
];

export interface RiskFlagTrigger {
  id: string;
  type: 'item-threshold' | 'score-threshold';
  /** For item-threshold: which item triggers the flag. */
  itemLinkId?: string;
  /** For score-threshold: which scoring output to compare against. */
  scoreOutputId?: string;
  threshold: number;
  comparison: RiskFlagComparison;
  flagCode: string;
  flagDisplay: string;
}

// ---------------------------------------------------------------------------
// Full scoring configuration
// ---------------------------------------------------------------------------

export interface ScoringConfiguration {
  outputs: ScoringOutput[];
  /** Interpretation bands keyed by scoring output id. */
  bands: Record<string, InterpretationBand[]>;
  riskFlags: RiskFlagTrigger[];
}

/** Supported instrument presets (license-safe, can be shipped in v1). */
export const INSTRUMENT_PRESETS = [
  'phq9', 'phq8', 'phq2', 'gad7', 'gad2', 'pcl5', 'des2',
] as const;

export type InstrumentPreset = (typeof INSTRUMENT_PRESETS)[number];

export const INSTRUMENT_PRESET_LABELS: Record<InstrumentPreset, string> = {
  phq9: 'PHQ-9 (Depression)',
  phq8: 'PHQ-8 (Depression, no suicidality item)',
  phq2: 'PHQ-2 (Depression screener)',
  gad7: 'GAD-7 (Anxiety)',
  gad2: 'GAD-2 (Anxiety screener)',
  pcl5: 'PCL-5 (PTSD)',
  des2: 'DES-II (Dissociation)',
};
